"use client"

import { Button } from "@/components/ui/button"
import Image from "next/image"
import Link from "next/link"
import iphone from "@/app/(landing)/img/iphone.png"
import blog from "@/app/(landing)/img/blog.png"
import { motion, useInView } from "framer-motion"
import { useRef } from "react"
import { standardScrollConfig } from "@/utils/animations"

import { ArrowRight } from "lucide-react"
import LoginButton from "@/app/components/LoginButton"

interface FeatureItemProps {
  icon: React.ReactNode
  title: string
  description: string
}

/**
 * HeroSection component with animated fade-in effects
 * Elements appear sequentially as the user scrolls into view
 */
export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, standardScrollConfig)

  // Animation variants for different elements
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  }

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        delay: 1,
        ease: "easeOut",
      },
    },
  }

  const blogVariant = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  }

  return (
    <section
      ref={sectionRef}
      className="bg-wood relative overflow-hidden  border-wood"
    >
      <div className="relative container grid gap-12 lg:grid-cols-[1fr_0.68fr]">
        <motion.div
          className="space-y-8 lg:space-y-12 xl:min-h-[800px] flex flex-col justify-center"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Heading and subheading */}
          <div className="mt-32 space-y-5">
            <motion.h1
              variants={itemVariants}
              className="font-ivyora tracking-[-1.44px] text-5xl lg:text-[64px] text-white text-center sm:text-left"
            >
              Where Ideas Thrive
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="text-white mt-3 text-lg font-base text-center sm:text-left"
            >
              Grow your audience & earnings — one post at a time.
            </motion.p>
          </div>

          {/* CTA buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row flex-wrap items-center gap-4 "
          >
            <LoginButton
              className="w-full sm:w-auto "
              variant="default"
              aria-label="Get started"
              redirectNewUserToOnboarding
            />
            <Link href="/home" className="w-full sm:w-auto">
              <Button
                variant="ghost"
                aria-label="Streamline raises $12M from Roba Ventures"
                className="text-white w-full sm:w-auto"
              >
                <span className="flex items-center gap-2 text-start whitespace-pre-wrap">
                  Start reading
                  <ArrowRight className="size-4 stroke-3" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </motion.div>

        <div className="relative -mr-[max(5rem,calc((100vw-80rem)/2+5rem))]  max-lg:translate-x-10 ">
          <div
            className="relative min-h-[30rem] flex flex-col justify-end"
            role="region"
            aria-roledescription="carousel"
          >
            <div className="overflow-visible relative sm:pt-32 sm:mt-16">
              <div className="flex">
                <div role="group" aria-roledescription="slide" className="">
                  <motion.div
                    className="relative sm:min-h-[20rem] h-full flex justify-end flex-col"
                    variants={imageVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                  >
                    <Image
                      alt="Hero"
                      src={iphone}
                      className="w-auto h-92 sm:h-192 z-10"
                      priority
                      fetchPriority="high"
                      width={868}
                      height={1392}
                    />
                  </motion.div>
                </div>
              </div>

              <motion.div
                className="absolute left-[25%] -top-32 sm:top-12 z-0"
                variants={blogVariant}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                <Image
                  alt="Blog"
                  src={blog}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 3000px"
                  className="w-auto max-w-none h-[860px]"
                  priority
                  fetchPriority="high"
                  width={4376}
                  height={3004}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
